import { useMutation, useQueryClient } from "react-query";
import { request } from "../../config/helpers/axios-instance";
import { throwIfRequestError } from "./request-error";

// useUpdateOrder — PUT /api/userOrder/admin/id/:id.
//
// This is the write path behind the Orders side panel's "Mark as Pre-sorted" /
// "Mark as Ready to Dispatch", and behind B2B's cancel-comment dropdowns.
//
// PAYLOAD DISCIPLINE MATTERS HERE, for two reasons:
//
//   1. `controller/userOrder.js:792` emits a loud console.warn whenever `partnerId` or
//      `partnerEntityId` appears in the body, before/after, because reassigning an
//      order's partner is a significant act. A status-only or comment-only save that
//      spreads a whole row into the body would trip that warning on every keystroke
//      and bury the real ones. So callers pass only the fields they mean to change,
//      and this hook forwards exactly that.
//
//   2. `metadata` is SHALLOW-MERGED server-side (`:807`), not replaced. Sending a
//      partial metadata object is safe; sending a stale full one silently reverts
//      whatever another writer just added.
//
// Fields the endpoint accepts are whitelisted server-side against UpdateAttributes;
// anything else is dropped silently rather than erroring, so a typo'd key looks like a
// successful no-op. Keep the allow-list below in step with what the screens actually
// need, and prefer failing loudly here over discovering it in production.

/** Fields these screens are allowed to write. Deliberately narrow. */
const WRITABLE = new Set(["status", "notes", "cancelledReason", "metadata", "startDate", "endDate", "noOfDays"]);

export default function useUpdateOrder() {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ orderId, changes }) => {
            const rejected = Object.keys(changes).filter((key) => !WRITABLE.has(key));
            if (rejected.length) {
                // A silent server-side drop would look like a successful save that did
                // nothing. Fail here instead, where the mistake is.
                throw new Error(`useUpdateOrder: refusing to send non-writable field(s): ${rejected.join(", ")}`);
            }

            const result = await request({
                url: `/userOrder/admin/id/${orderId}`,
                method: "PUT",
                data: changes,
            });
            throwIfRequestError(result);
            const body = result.data;
            if (!body?.success) throw body;
            return body.data;
        },
        {
            onSettled: () => {
                // Re-read rather than patching the cache: a status change can move a row
                // between sub-tabs, and the server is the only thing that knows where.
                queryClient.invalidateQueries(["ops-orders"]);
                queryClient.invalidateQueries(["ops-overview"]);
            },
        }
    );
}
